import { useMemo, useState, type MouseEvent } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { Connection, LiteratureStateData } from "../sockets-client";
import { CardDTO, type PlayerInfo } from "../interface/GameTypes";
import {
  LiteratureSetId,
  LITERATURE_SET_LABELS,
  cardsInLiteratureSet,
  literatureCardKey,
  sameLiteratureHalfSuit,
} from "../interface/literatureSets";
import { Toast } from "./Toast";
import { ReactionMenu } from "./ReactionMenu";
import { ReactionOverlay } from "./ReactionOverlay";
import { TableGiphyOverlay } from "./TableGiphyOverlay";
import { TableGiphyPickerDialog } from "./TableGiphyPickerDialog";
import { useTableSocialSocket } from "../hooks/useTableSocialSocket";

type Props = {
  state: LiteratureStateData;
  myUUID: string;
  socketConnection: Connection;
  onReturnToLobby: () => void;
};

function cardLabel(c: CardDTO): string {
  if (c.suit === "Joker") return c.rank === "Red" ? "RJ" : "BJ";
  return `${c.rank}${c.suit[0]}`;
}

function candidateAskCards(myHand: CardDTO[], unclaimedSets: string[]): CardDTO[] {
  const key = (c: CardDTO) => literatureCardKey(c.suit, c.rank);
  const held = new Set(myHand.map(key));
  const candidates = new Map<string, CardDTO>();
  for (const setId of unclaimedSets) {
    for (const c of cardsInLiteratureSet(setId as LiteratureSetId)) {
      if (held.has(key(c))) continue;
      const hasSameSet = myHand.some((h) => sameLiteratureHalfSuit(h, c));
      if (hasSameSet) candidates.set(key(c), c);
    }
  }
  return Array.from(candidates.values());
}

export const LiteratureTable = ({
  state,
  myUUID,
  socketConnection,
  onReturnToLobby,
}: Props) => {
  const [askTarget, setAskTarget] = useState<number | null>(null);
  const [declareOpen, setDeclareOpen] = useState(false);
  const [declareSetId, setDeclareSetId] = useState<LiteratureSetId | "">("");
  const [declareSeats, setDeclareSeats] = useState<Record<string, number>>({});

  const social = useTableSocialSocket(socketConnection);

  const openReactionMenuForPlayer = (p: PlayerInfo, e: MouseEvent<HTMLElement>) => {
    if (p.isBot || p.uuid === myUUID) return;
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    social.setReactionMenuTarget({
      playerUUID: p.uuid,
      playerName: p.name ?? "Player",
      anchorX: r.left + r.width / 2,
      anchorY: r.top,
    });
  };

  const mySeat = useMemo(
    () => state.players.findIndex((p) => p.uuid === myUUID),
    [state.players, myUUID]
  );
  const myTeam = mySeat >= 0 ? state.players.find((p) => p.seatIndex === mySeat)?.teamId ?? null : null;
  const isMyTurn =
    state.phase === "PLAYING" && mySeat >= 0 && state.currentTurnSeat === mySeat;

  const opponents = useMemo(() => {
    if (myTeam === null || mySeat < 0) return [];
    return state.players
      .map((p, seat) => ({ p, seat }))
      .filter(
        ({ p, seat }) => p.teamId !== myTeam && (state.handSizes[seat] ?? 0) > 0
      );
  }, [state.players, state.handSizes, myTeam, mySeat]);

  const askCards = useMemo(
    () => candidateAskCards(state.myHand, state.unclaimedSets),
    [state.myHand, state.unclaimedSets]
  );

  const openDeclare = () => {
    const first = state.unclaimedSets[0] as LiteratureSetId | undefined;
    setDeclareSetId(first ?? "");
    setDeclareSeats({});
    setDeclareOpen(true);
  };

  const declareSetCards = declareSetId ? cardsInLiteratureSet(declareSetId) : [];

  const setSeatForCard = (card: CardDTO, seat: number) => {
    setDeclareSeats((prev) => ({
      ...prev,
      [literatureCardKey(card.suit, card.rank)]: seat,
    }));
  };

  const submitDeclare = () => {
    if (!declareSetId) return;
    const assignments: Record<string, number> = { ...declareSeats };
    for (const c of declareSetCards) {
      const k = literatureCardKey(c.suit, c.rank);
      if (assignments[k] === undefined) return;
    }
    socketConnection.literatureDeclare(declareSetId, assignments);
    setDeclareOpen(false);
  };

  const gameOver = state.phase === "GAME_OVER" && state.winner;
  const scoringLabel =
    state.scoringMode === "weighted" ? "Weighted (major 2, minor 1)" : "Standard (all sets 1)";

  return (
    <Box sx={{ p: { xs: 1, md: 2 }, maxWidth: 1200, mx: "auto" }} data-testid="literature-table">
      <Stack spacing={1.5}>
        <Paper
          elevation={6}
          sx={{
            p: 1.5,
            backgroundColor: "rgba(20,20,28,0.92)",
            color: "#f0f0f0",
            border: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
            <Box>
              <Typography variant="h5">Literature</Typography>
              <Typography variant="caption" sx={{ opacity: 0.75 }}>
                Scoring: {scoringLabel}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                size="small"
                variant="outlined"
                onClick={() => social.setGiphyPickerOpen(true)}
                disabled={social.giphyCooldownMs > 0}
                color="inherit"
              >
                GIF
              </Button>
              <Button size="small" onClick={onReturnToLobby} color="inherit">
                Leave
              </Button>
            </Stack>
          </Stack>
          <Stack direction="row" spacing={2} sx={{ mt: 1 }} data-testid="literature-scoreboard">
            <Chip label={`Team A: ${state.teamScores.A}`} color="primary" />
            <Chip label={`Team B: ${state.teamScores.B}`} color="secondary" />
            <Chip
              label={state.phase === "PLAYING" ? `Turn: Seat ${state.currentTurnSeat + 1}` : "Game Over"}
              variant="outlined"
            />
          </Stack>
        </Paper>

        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
          <Paper
            elevation={4}
            sx={{
              p: 1.5,
              flex: 1,
              backgroundColor: "rgba(15,17,23,0.9)",
              border: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Players
            </Typography>
            <Stack spacing={1} sx={{ mb: 2 }}>
              {state.players.map((p, i) => (
                <Stack
                  key={p.uuid}
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  onClick={(e) => openReactionMenuForPlayer(p, e)}
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    backgroundColor:
                      state.currentTurnSeat === i && state.phase === "PLAYING"
                        ? "rgba(255,215,0,0.12)"
                        : "rgba(255,255,255,0.04)",
                    border:
                      p.uuid === myUUID ? "1px solid rgba(100,200,255,0.5)" : "1px solid transparent",
                    cursor: p.isBot || p.uuid === myUUID ? "default" : "pointer",
                  }}
                >
                  <Typography sx={{ minWidth: 24 }}>{i + 1}</Typography>
                  <Typography flex={1}>
                    {p.name ?? "Player"} (Team {p.teamId})
                    {p.uuid === myUUID ? " — you" : ""}
                  </Typography>
                  <Chip size="small" label={`${state.handSizes[i] ?? 0} cards`} />
                </Stack>
              ))}
            </Stack>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Last Ask
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85 }} data-testid="literature-last-ask">
              {state.lastAsk
                ? `Seat ${state.lastAsk.askerSeat + 1} asked Seat ${
                    state.lastAsk.targetSeat + 1
                  } for ${cardLabel(state.lastAsk.card)} — ${state.lastAsk.hit ? "Hit" : "Miss"}`
                : "No asks yet."}
            </Typography>
          </Paper>

          <Paper
            elevation={4}
            sx={{
              p: 1.5,
              flex: 1.1,
              backgroundColor: "rgba(15,17,23,0.9)",
              border: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Your Hand
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 2 }} data-testid="literature-my-hand">
              {state.myHand.map((c, idx) => (
                <Chip
                  key={`${literatureCardKey(c.suit, c.rank)}-${idx}`}
                  label={cardLabel(c)}
                  size="small"
                />
              ))}
            </Stack>

            {gameOver ? (
              <Stack spacing={1}>
                <Typography color="secondary.main" data-testid="literature-game-over">
                  Game over — Team {state.winner} wins!
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => socketConnection.playAgain(false)}
                  data-testid="literature-play-again"
                >
                  Play again
                </Button>
              </Stack>
            ) : isMyTurn ? (
              <Stack spacing={2} data-testid="literature-turn-panel">
                <Typography variant="subtitle1">Your turn</Typography>
                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Ask an opponent
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
                    {opponents.map(({ p, seat }) => (
                      <Button
                        key={p.uuid}
                        size="small"
                        variant={askTarget === seat ? "contained" : "outlined"}
                        color="inherit"
                        onClick={() => setAskTarget(seat)}
                      >
                        {p.name ?? `Seat ${seat + 1}`}
                      </Button>
                    ))}
                  </Stack>
                  {askTarget !== null && (
                    <Stack direction="row" flexWrap="wrap" gap={0.5} data-testid="literature-ask-cards">
                      {askCards.map((c) => (
                        <Button
                          key={literatureCardKey(c.suit, c.rank)}
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            socketConnection.literatureAsk(askTarget, c.suit, c.rank);
                            setAskTarget(null);
                          }}
                        >
                          {cardLabel(c)}
                        </Button>
                      ))}
                    </Stack>
                  )}
                </Box>
                <Button variant="contained" color="secondary" onClick={openDeclare}>
                  Declare a set
                </Button>
              </Stack>
            ) : (
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Waiting for your turn.
              </Typography>
            )}
          </Paper>
        </Stack>
      </Stack>

      {social.reactionAnnounce && (
        <Toast
          message={social.reactionAnnounce}
          severity="info"
          onClose={() => social.setReactionAnnounce(null)}
        />
      )}
      <ReactionOverlay reactions={social.reactionQueue} onComplete={social.handleReactionComplete} />
      <TableGiphyOverlay queue={social.giphyQueue} onDismissHead={social.dismissGiphyHead} />
      <TableGiphyPickerDialog
        open={social.giphyPickerOpen}
        onClose={() => social.setGiphyPickerOpen(false)}
        onPick={social.shareGiphy}
      />
      {social.reactionMenuTarget && (
        <ReactionMenu
          anchorX={social.reactionMenuTarget.anchorX}
          anchorY={social.reactionMenuTarget.anchorY}
          targetName={social.reactionMenuTarget.playerName}
          cooldownMs={social.reactionCooldownMs}
          onSelect={social.handleReactionSelect}
          onClose={social.closeReactionMenu}
        />
      )}

      <Dialog open={declareOpen} onClose={() => setDeclareOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Declare a set</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
            <InputLabel>Set</InputLabel>
            <Select
              label="Set"
              value={declareSetId}
              onChange={(e) => {
                setDeclareSetId(e.target.value as LiteratureSetId);
                setDeclareSeats({});
              }}
            >
              {state.unclaimedSets.map((id) => (
                <MenuItem key={id} value={id}>
                  {LITERATURE_SET_LABELS[id as LiteratureSetId] ?? id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {declareSetCards.map((c) => (
            <FormControl key={literatureCardKey(c.suit, c.rank)} fullWidth sx={{ mb: 1 }}>
              <InputLabel>{cardLabel(c)}</InputLabel>
              <Select
                label={cardLabel(c)}
                value={
                  declareSeats[literatureCardKey(c.suit, c.rank)] ?? ""
                }
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "") return;
                  setSeatForCard(c, Number(v));
                }}
              >
                <MenuItem value="">
                  <em>Choose seat</em>
                </MenuItem>
                {state.players.map((p, seat) => (
                  <MenuItem key={p.uuid} value={seat}>
                    Seat {seat + 1} — {p.name ?? "?"} (Team {p.teamId})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeclareOpen(false)}>Cancel</Button>
          <Button
            onClick={submitDeclare}
            variant="contained"
            disabled={
              !declareSetId ||
              declareSetCards.some(
                (c) => declareSeats[literatureCardKey(c.suit, c.rank)] === undefined
              )
            }
          >
            Submit declare
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
